/**
 * x-marquee directive.
 *
 * Creates an infinite horizontal marquee animation using GSAP.
 * Supports variable width items by cloning content to fill the viewport.
 *
 * @options
 *   speed          - Pixels per second (default: 50).
 *   reverse        - Reverse direction (right to left becomes left to right).
 *   pauseOnHover   - Pause animation on mouse hover.
 *   onlyIfOverflow - Only animate when content overflows the container.
 *   draggable      - Allow drag/swipe interaction (default: false).
 *   fullWidth      - Use window width instead of container width for cloning (default: false).
 *
 * @accessibility
 *   - Respects prefers-reduced-motion (skips animation; fallback should be handled with tailwind motion-reduce:*).
 *   - Pauses when focusable elements inside receive focus.
 *   - Cloned items have aria-hidden="true" to prevent duplicate screen reader readings.
 *
 * @api (exposed on element._marquee)
 *   - stop()                 - Pause the animation.
 *   - play()                 - Resume the animation.
 *   - goto(target, animate)  - Scroll to item (index, element, or selector). animate defaults to true.
 *
 * @example
 *   <div x-marquee="{ speed: 100, pauseOnHover: true, onlyIfOverflow: true, draggable: true }">
 *     <div x-marquee:track>…</div>
 *   </div>
 *
 *   // External control:
 *   document.querySelector('[x-marquee]')._marquee.goto(2);
 *   document.querySelector('[x-marquee]')._marquee.stop();
 */
(() => {
  const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

  const root = (Alpine, el, options = {}) => {
    const data = Alpine.$data(el);
    const gsap = Alpine.raw(data.$gsap || window.gsap);
    const Draggable = Alpine.raw(data.$draggable || window.Draggable);
    const InertiaPlugin = Alpine.raw(data.$inertia || window.InertiaPlugin);

    if (!gsap) {
      console.warn(
        "x-marquee: GSAP not found. Make sure $gsap magic is available.",
      );
      return;
    }

    const {
      speed = 50,
      reverse = false,
      pauseOnHover = false,
      onlyIfOverflow = false,
      draggable = false,
      fullWidth = false,
    } = options;

    const track = el.querySelector("[x-marquee\\:track]");

    if (!track) {
      console.warn("x-marquee: No [x-marquee\\:track] element found.");
      return;
    }

    // State.
    let tween = null;
    let clones = [];
    let totalWidth = 0;
    let draggableInstance = null;
    let proxy = null;
    let resizeObserver = null;

    let isDragging = false;
    let isPausedFromDrag = false;
    let isHovering = false;
    let isVisible = false;
    let isFocused = false;
    let isManuallyPaused = false;
    let originalItems = [];
    let containerLeft = 0;
    let initialX = 0;

    const motionQuery = window.matchMedia(REDUCED_MOTION_QUERY);
    let prefersReducedMotion = motionQuery.matches;

    /**
     * Wraps a progress value to the 0–1 range, handling negative values.
     * @param {number} val - The raw progress value.
     * @returns {number} The wrapped value between 0 and 1.
     */
    const wrapProgress = (val) => ((val % 1) + 1) % 1;

    /**
     * Returns true when all conditions allow the tween to play.
     * @returns {boolean} True when the marquee should be playing.
     */
    const shouldPlay = () =>
      isVisible &&
      !isHovering &&
      !isDragging &&
      !isFocused &&
      !isManuallyPaused &&
      !prefersReducedMotion;

    /**
     * Resumes the tween if all play conditions are met.
     */
    const resumeIfAllowed = () => {
      if (shouldPlay()) tween?.play();
    };

    /**
     * Creates an aria-hidden clone of the given item and appends it to the
     * clones array for later cleanup.
     * @param {Element} item - The original track item to clone.
     * @returns {Element} The cloned element.
     */
    const createClone = (item) => {
      const clone = item.cloneNode(true);
      clone.setAttribute("aria-hidden", "true");
      clone.dataset.marqueeClone = "";
      clones.push(clone);
      return clone;
    };

    /**
     * Called when a drag interaction ends. Resumes the tween unless the
     * pointer is still hovering over the container.
     */
    const handleDragEnd = () => {
      isDragging = false;
      if (!isHovering && isVisible) {
        resumeIfAllowed();
      } else {
        isPausedFromDrag = true;
      }
    };

    /**
     * Kills the active tween and draggable instance, removes all clones, and
     * resets the track's x position to 0.
     */
    const reset = () => {
      tween?.kill();
      tween = null;

      draggableInstance?.[0]?.kill();
      draggableInstance = null;

      proxy?.remove();
      proxy = null;

      clones.forEach((clone) => clone.remove());
      clones = [];

      gsap.set(track, { x: 0 });
    };

    /**
     * Resets the marquee, clones items to fill the container, then creates the
     * GSAP tween. Also sets up the Draggable instance if `draggable` is enabled.
     */
    const init = () => {
      reset();

      originalItems = Array.from(track.children).filter(
        (item) => !item.hasAttribute("data-marquee-clone"),
      );

      const trackWidth = track.scrollWidth;
      const containerWidth = fullWidth ? window.innerWidth : el.offsetWidth;
      const hasOverflow = trackWidth > containerWidth;

      el.classList.toggle("is-overflow", hasOverflow);

      if (prefersReducedMotion) return;
      if (onlyIfOverflow && !hasOverflow) return;

      containerLeft = fullWidth ? el.getBoundingClientRect().left : 0;

      // Prepend clones to fill the window left gap in fullWidth mode.
      if (fullWidth && containerLeft > 0) {
        const preClonesNeeded = Math.ceil(containerLeft / trackWidth);
        const reversedItems = [...originalItems].reverse();

        for (let i = 0; i < preClonesNeeded; i++) {
          reversedItems.forEach((item) => track.prepend(createClone(item)));
        }
      }

      // Append clones for seamless looping.
      const clonesNeeded = Math.ceil(containerWidth / trackWidth);

      for (let i = 0; i < clonesNeeded; i++) {
        originalItems.forEach((item) => track.appendChild(createClone(item)));
      }

      totalWidth = trackWidth;
      const duration = totalWidth / speed;
      const direction = reverse ? 1 : -1;

      // Calculate initial position for fullWidth alignment.
      initialX = 0;
      if (fullWidth && originalItems[0]) {
        const firstOriginal = originalItems[0];
        const firstOriginalLeft = firstOriginal.getBoundingClientRect().left;
        const paddingLeft =
          parseFloat(getComputedStyle(firstOriginal).paddingLeft) || 0;
        initialX = containerLeft - firstOriginalLeft - paddingLeft;
      }

      gsap.set(track, { x: initialX });

      tween = gsap.to(track, {
        x: initialX + totalWidth * direction,
        duration,
        ease: "none",
        repeat: -1,
        modifiers: {
          x: gsap.utils.unitize((x) => {
            const offset = parseFloat(x) - initialX;
            return (offset % totalWidth) + initialX;
          }),
        },
      });

      if (!shouldPlay()) tween.pause();

      // Set up draggable.
      if (draggable && Draggable) {
        proxy = document.createElement("div");
        let ratio, startProgress;

        const align = (instance) => {
          if (!tween) return;
          tween.progress(
            wrapProgress(
              startProgress + (instance.startX - instance.x) * ratio,
            ),
          );
        };

        draggableInstance = Draggable.create(proxy, {
          trigger: el,
          type: "x",
          inertia: !!InertiaPlugin,
          allowContextMenu: true,
          onPressInit() {
            if (!tween) return;
            gsap.killTweensOf(tween);
            isDragging = true;
            tween.pause();
            startProgress = tween.progress();
            ratio = 1 / totalWidth;
            gsap.set(proxy, { x: startProgress / -ratio });
          },
          onDrag() {
            align(this);
          },
          onThrowUpdate() {
            align(this);
          },
          onRelease() {
            if (!InertiaPlugin) handleDragEnd();
          },
          onThrowComplete: handleDragEnd,
        });
      }
    };

    // Expose API on the element; methods gracefully handle the case where no tween exists.
    el._marquee = {
      /**
       * Pauses the marquee animation.
       */
      stop() {
        isManuallyPaused = true;
        tween?.pause();
      },

      /**
       * Resumes the marquee animation if all play conditions are met.
       */
      play() {
        isManuallyPaused = false;
        resumeIfAllowed();
      },

      /**
       * Scrolls the marquee to bring the target item to the container's left edge.
       * @param {number|string|Element} target - Item index, CSS selector, or element.
       * @param {boolean} [animate=true] - Whether to animate the transition.
       */
      goto(target, animate = true) {
        if (!originalItems.length) return;

        // Resolve target to element.
        let targetEl;
        if (typeof target === "number") {
          targetEl = originalItems[target];
        } else if (typeof target === "string") {
          targetEl = track.querySelector(target);
        } else {
          targetEl = target;
        }

        if (!targetEl || !track.contains(targetEl)) return;

        // Calculate position to align target with container left.
        const elLeft = fullWidth ? el.getBoundingClientRect().left : 0;
        const targetLeft = targetEl.getBoundingClientRect().left;
        const paddingLeft =
          parseFloat(getComputedStyle(targetEl).paddingLeft) || 0;
        const currentX = gsap.getProperty(track, "x") || 0;
        const targetX = currentX + (elLeft - targetLeft - paddingLeft);

        tween?.pause();

        if (animate) {
          gsap.to(track, {
            x: targetX,
            duration: 0.5,
            ease: "power2.out",
            onComplete: () => {
              if (!tween) return;
              // Sync tween progress with new position.
              const offset = targetX - initialX;
              const progress = wrapProgress(-offset / totalWidth);
              tween.progress(progress);
            },
          });
        } else {
          gsap.set(track, { x: targetX });
          if (tween) {
            const offset = targetX - initialX;
            const progress = wrapProgress(-offset / totalWidth);
            tween.progress(progress);
          }
        }
      },
    };

    // Handler references for cleanup.
    let initialized = false;
    let delayedInitCall = null;

    /**
     * Handles changes to the prefers-reduced-motion media query by
     * re-initialising the marquee and resuming if allowed.
     * @param {MediaQueryListEvent} e - The media query change event.
     */
    const handleMotionChange = (e) => {
      prefersReducedMotion = e.matches;
      init();
      resumeIfAllowed();
    };

    Alpine.bind(el, () => ({
      ["x-data"]() {
        return {
          /**
           * Attaches the motion query listener and a ResizeObserver to
           * re-initialise the marquee when the container resizes.
           */
          init() {
            motionQuery.addEventListener("change", handleMotionChange);

            resizeObserver = new ResizeObserver(() => {
              if (!initialized) return;
              delayedInitCall?.kill();
              delayedInitCall = gsap.delayedCall(0.1, init);
            });
            resizeObserver.observe(this.$el);
          },

          /**
           * Resets the marquee, disconnects observers, and removes the public API.
           */
          destroy() {
            reset();
            resizeObserver?.disconnect();
            motionQuery.removeEventListener("change", handleMotionChange);
            delayedInitCall?.kill();
            delete el._marquee;
          },
        };
      },
      ["@mouseenter"]() {
        if (!pauseOnHover) return;
        isHovering = true;
        if (!isDragging) tween?.pause();
      },
      ["@mouseleave"]() {
        isHovering = false;
        if (isPausedFromDrag) {
          isPausedFromDrag = false;
          if (isVisible) tween?.play();
        } else if (pauseOnHover) {
          resumeIfAllowed();
        }
      },
      ["@focusin"]() {
        isFocused = true;
        tween?.pause();
      },
      ["@focusout"](e) {
        if (!el.contains(e.relatedTarget)) {
          isFocused = false;
          resumeIfAllowed();
        }
      },
      ["x-intersect:enter"]() {
        isVisible = true;
        if (!initialized) {
          initialized = true;
          init();
        }
        resumeIfAllowed();
      },
      ["x-intersect:leave"]() {
        isVisible = false;
        tween?.pause();
      },
    }));
  };

  document.addEventListener("alpine:init", () => {
    Alpine.directive("marquee", (el, { expression, value }, { evaluate }) => {
      if (value === "track") return;
      root(Alpine, el, expression ? evaluate(expression) : {});
    });
  });
})();
